import {
  useQuery
} from "@tanstack/react-query";

import {
  Gift,
  LoaderCircle,
  PackageCheck,
  Sparkles
} from "lucide-react";

import {
  Link
} from "react-router";

import {
  ProductCard
} from "../components/product/ProductCard";

import {
  getProducts
} from "../services/catalog";

export function GiftHampersPage() {
  const hampersQuery =
    useQuery({
      queryKey: [
        "products",
        "gift-hampers"
      ],

      queryFn: () =>
        getProducts(
          "gift-hampers"
        )
    });

  const hampers =
    hampersQuery.data ?? [];

  return (
    <main>
      {/* ================================================== */}
      {/* HERO */}
      {/* ================================================== */}

      <section className="overflow-hidden bg-[#efe3d2]">
        <div className="rini-container py-20 sm:py-24 lg:py-28">
          <div className="mx-auto max-w-4xl text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-white/80 text-[#6f402d] shadow-sm">
              <Gift
                size={25}
                strokeWidth={1.5}
              />
            </span>

            <p className="mt-7 text-sm font-semibold uppercase tracking-[0.24em] text-[#70845b]">
              Gifting with RiNi
            </p>

            <h1 className="rini-heading mt-4 text-5xl font-semibold leading-tight sm:text-6xl lg:text-7xl">
              Thoughtful gifts,
              <br className="hidden sm:block" />
              naturally beautiful
            </h1>

            <p className="mx-auto mt-7 max-w-2xl text-lg leading-8 text-[#746d65]">
              Curated RiNi bodycare collections made for
              birthdays, celebrations, weddings, festivals,
              return gifts and meaningful moments.
            </p>

            <div className="mt-9 flex flex-wrap justify-center gap-4">
              <a
                href="#gift-hampers"
                className="rini-button-primary"
              >
                Explore hampers
              </a>

              <Link
                to="/shop"
                className="rini-button-secondary"
              >
                Shop all products
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================== */}
      {/* VALUE STRIP */}
      {/* ================================================== */}

      <section className="border-b border-[#5e3b2c]/10 bg-[#fffdf8]">
        <div className="rini-container grid gap-4 py-8 md:grid-cols-3">
          <article className="flex items-start gap-4 rounded-2xl p-4">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#edf1e8] text-[#70845b]">
              <Sparkles size={19} />
            </span>

            <div>
              <h2 className="font-semibold text-[#38251e]">
                Thoughtfully curated
              </h2>

              <p className="mt-1 text-sm leading-6 text-[#746d65]">
                RiNi favourites brought together as one
                meaningful gifting experience.
              </p>
            </div>
          </article>

          <article className="flex items-start gap-4 rounded-2xl p-4">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#edf1e8] text-[#70845b]">
              <PackageCheck size={19} />
            </span>

            <div>
              <h2 className="font-semibold text-[#38251e]">
                Ready for gifting
              </h2>

              <p className="mt-1 text-sm leading-6 text-[#746d65]">
                Carefully arranged collections designed for
                celebrations and special occasions.
              </p>
            </div>
          </article>

          <article className="flex items-start gap-4 rounded-2xl p-4">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#edf1e8] text-[#70845b]">
              <Gift size={19} />
            </span>

            <div>
              <h2 className="font-semibold text-[#38251e]">
                RiNi inside
              </h2>

              <p className="mt-1 text-sm leading-6 text-[#746d65]">
                Each hamper can contain products from the
                existing RiNi bodycare collection.
              </p>
            </div>
          </article>
        </div>
      </section>

      {/* ================================================== */}
      {/* CATALOG */}
      {/* ================================================== */}

      <section
        id="gift-hampers"
        className="rini-section bg-[#fffdf8]"
      >
        <div className="rini-container">
          <div className="flex flex-col gap-5 border-b border-[#5e3b2c]/10 pb-8 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.22em] text-[#70845b]">
                The gift collection
              </p>

              <h2 className="rini-heading mt-3 text-4xl font-semibold sm:text-5xl">
                RiNi Gift Hampers
              </h2>

              <p className="mt-4 max-w-2xl leading-7 text-[#746d65]">
                Explore our current gift hamper collection.
                Open any hamper to discover the RiNi products
                included inside.
              </p>
            </div>

            {!hampersQuery.isLoading && (
              <p className="text-sm font-medium text-[#746d65]">
                {hampers.length}
                {" "}
                {hampers.length === 1
                  ? "hamper"
                  : "hampers"}
              </p>
            )}
          </div>

          {hampersQuery.isLoading && (
            <div className="flex min-h-96 items-center justify-center">
              <LoaderCircle
                size={36}
                className="animate-spin text-[#70845b]"
              />
            </div>
          )}

          {hampersQuery.isError && (
            <div className="my-12 rounded-[2rem] bg-[#f7e6e2] p-10 text-center text-[#713f36]">
              <h3 className="font-serif text-2xl">
                Gift hampers could not be loaded
              </h3>

              <p className="mt-2">
                Please refresh the page and try again.
              </p>
            </div>
          )}

          {!hampersQuery.isLoading &&
            !hampersQuery.isError &&
            hampers.length > 0 && (
              <div className="mt-10 grid gap-7 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {hampers.map(
                  (hamper) => (
                    <ProductCard
                      key={hamper.id}
                      product={hamper}
                    />
                  )
                )}
              </div>
            )}

          {!hampersQuery.isLoading &&
            !hampersQuery.isError &&
            hampers.length === 0 && (
              <div className="py-24 text-center">
                <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#edf1e8] text-[#70845b]">
                  <Gift size={28} />
                </span>

                <h3 className="rini-heading mt-6 text-3xl">
                  New gift hampers are coming soon
                </h3>

                <p className="mx-auto mt-3 max-w-xl leading-7 text-[#746d65]">
                  RiNi is preparing thoughtful collections
                  for your special moments.
                </p>

                <Link
                  to="/shop"
                  className="rini-button-primary mt-8"
                >
                  Explore RiNi products
                </Link>
              </div>
            )}
        </div>
      </section>

      {/* ================================================== */}
      {/* CUSTOM GIFTING MESSAGE */}
      {/* ================================================== */}

      <section className="bg-[#edf1e8]">
        <div className="rini-container py-16 sm:py-20">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-sm font-semibold uppercase tracking-[0.22em] text-[#70845b]">
              More ways to gift
            </p>

            <h2 className="rini-heading mt-4 text-4xl font-semibold sm:text-5xl">
              Made for meaningful occasions
            </h2>

            <p className="mx-auto mt-5 max-w-2xl leading-8 text-[#746d65]">
              RiNi hampers can be thoughtfully selected for
              birthdays, weddings, return gifts, festivals,
              celebrations and corporate gifting.
            </p>
          </div>
        </div>
      </section>
    </main>
  );
}
